import { useState, useCallback, useMemo } from 'react';

interface SidebarProjectPreferences {
  pinnedProjectIds: string[];
  projectOrder: string[];
  /** "My projects" scope toggle: true → only the user's projects, false → all. */
  mineOnly: boolean;
}

const DEFAULT_PREFS: SidebarProjectPreferences = {
  pinnedProjectIds: [],
  projectOrder: [],
  // Default ON so a first-time user (and anyone who can view all projects)
  // lands scoped to their own projects.
  mineOnly: true,
};

function getStorageKey(userId: string) {
  return `clarix_sidebar_prefs_${userId}`;
}

function readPrefs(userId: string): SidebarProjectPreferences {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as SidebarProjectPreferences;
    return {
      pinnedProjectIds: Array.isArray(parsed.pinnedProjectIds) ? parsed.pinnedProjectIds : [],
      projectOrder: Array.isArray(parsed.projectOrder) ? parsed.projectOrder : [],
      // Only an explicitly stored `false` un-checks it — prefs written before
      // this field existed (undefined) keep the default-ON behaviour.
      mineOnly: typeof parsed.mineOnly === 'boolean' ? parsed.mineOnly : DEFAULT_PREFS.mineOnly,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(userId: string, prefs: SidebarProjectPreferences) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(prefs));
}

export function useSidebarProjectPreferences(userId: string) {
  const [preferences, setPreferences] = useState<SidebarProjectPreferences>(() =>
    readPrefs(userId),
  );

  const togglePin = useCallback(
    (projectId: string) => {
      setPreferences((prev) => {
        const isPinned = prev.pinnedProjectIds.includes(projectId);
        const next: SidebarProjectPreferences = {
          ...prev,
          pinnedProjectIds: isPinned
            ? prev.pinnedProjectIds.filter((id) => id !== projectId)
            : [...prev.pinnedProjectIds, projectId],
        };
        writePrefs(userId, next);
        return next;
      });
    },
    [userId],
  );

  const reorder = useCallback(
    (projectIds: string[]) => {
      setPreferences((prev) => {
        const next: SidebarProjectPreferences = { ...prev, projectOrder: projectIds };
        writePrefs(userId, next);
        return next;
      });
    },
    [userId],
  );

  /**
   * Persist an explicit "My projects" tick/untick. Call this only for a real
   * user action — an automatic/incidental scope switch should stay in component
   * state so it never overwrites what the user chose.
   */
  const setMineOnly = useCallback(
    (mineOnly: boolean) => {
      setPreferences((prev) => {
        const next: SidebarProjectPreferences = { ...prev, mineOnly };
        writePrefs(userId, next);
        return next;
      });
    },
    [userId],
  );

  const isPinned = useCallback(
    (projectId: string) => preferences.pinnedProjectIds.includes(projectId),
    [preferences.pinnedProjectIds],
  );

  return useMemo(
    () => ({ preferences, togglePin, reorder, setMineOnly, isPinned }),
    [preferences, togglePin, reorder, setMineOnly, isPinned],
  );
}
